import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRight, ArrowDown, ShieldCheck, Award, HardHat } from 'lucide-react';
import { Page, Reveal, MaskedLines, SectionTag } from '../components/motion';
import { Marquee } from '../components/Marquee';
import { ProjectCard } from '../components/ProjectCard';
import { CTASection } from '../components/CTASection';
import { COMPANY, SERVICES, PROJECTS, CLIENTS, IMAGES } from '../data/content';
import { useSEO } from '../hooks/useSEO';
import { useState } from 'react';

const Hero = () => {
  const ref = useRef(null);
  const firstVisit =
    typeof window !== 'undefined' &&
    !sessionStorage.getItem('gis-intro') &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const d = firstVisit ? 1.75 : 0;
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const featured = PROJECTS.find((p) => p.status === 'On-Going');

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <img
          src={IMAGES.hero}
          alt="Ehimo Building & Civil Engineering — Lekki, Lagos residential project at dusk"
          className="w-full h-[120%] object-cover saturate-125 contrast-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/45 via-transparent to-transparent" />
      </motion.div>

      <div className="absolute inset-0 flex" aria-hidden="true">
        {Array.from({ length: 7 }).map((_, i) => (
          <motion.div
            key={i}
            className="flex-1 bg-ink origin-top border-r border-ink/40 last:border-r-0"
            initial={{ scaleY: 1 }}
            animate={{ scaleY: 0 }}
            transition={{ duration: 0.8, delay: d + i * 0.07, ease: [0.65, 0, 0.35, 1] }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pt-40 pb-16">
        <Reveal y={14} className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-accent flex items-center gap-3">
            <span className="w-8 h-px bg-accent inline-block" aria-hidden="true" />
            {COMPANY.tagline} — Nigeria
          </p>
        </Reveal>

        <MaskedLines
          lines={['Buildings that', 'redefine Lagos,', 'Abuja & beyond.']}
          delay={0.15 + d}
          lineClassName="font-display text-[7vw] sm:text-6xl lg:text-[4.75rem] font-semibold uppercase tracking-tight leading-[0.92]"
        />

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <Reveal delay={0.6 + d} className="max-w-xl">
            <p className="text-mute text-base lg:text-lg leading-relaxed">{COMPANY.intro}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 bg-accent text-ink font-display font-medium uppercase tracking-wide px-8 py-4 text-sm transition-colors duration-300 hover:bg-accent-dim"
              >
                Start a Project{' '}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              <Link
                to="/projects"
                data-testid="hero-cta-secondary"
                className="group inline-flex items-center gap-3 border border-white/30 text-white font-display font-medium uppercase tracking-wide px-8 py-4 text-sm transition-colors duration-300 hover:border-accent hover:text-accent"
              >
                View Projects
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>

          {featured && (
            <Reveal delay={0.8 + d} className="lg:text-right">
              <Link to={`/projects/${featured.slug}`} data-testid="hero-featured-project" className="group inline-block">
                <p className="text-[10px] uppercase tracking-[0.3em] text-mute mb-2">Now on site</p>
                <p className="font-display text-lg uppercase tracking-tight group-hover:text-accent transition-colors">{featured.name}</p>
                <p className="text-xs text-accent uppercase tracking-[0.2em] mt-1">{featured.status}</p>
              </Link>
            </Reveal>
          )}
        </div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-mute"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden="true"
      >
        <ArrowDown size={16} />
      </motion.div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="bg-paper text-ink">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-7">
        <SectionTag number="01" label="Who we are" light />
        <MaskedLines
          inView
          lines={['A dynamic building & civil', 'engineering construction', 'company.']}
          lineClassName="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight leading-[1.02]"
        />
        <Reveal delay={0.2} className="mt-8 max-w-xl space-y-5">
          <p className="text-inkmute leading-relaxed">{COMPANY.reach}</p>
          <p className="text-inkmute leading-relaxed">{COMPANY.leadership}</p>
          <Link
            to="/about"
            data-testid="intro-about-cta"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors pt-2"
          >
            About EhimoBCE <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <Reveal delay={0.3} className="mt-12 max-w-xl grid grid-cols-3 border-t border-paperline pt-8">
          {[
            { value: '11+', label: 'Projects delivered' },
            { value: '27+', label: 'Years combined experience' },
            { value: '5', label: 'Cities across Nigeria' },
          ].map((stat) => (
            <div key={stat.label} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="font-display text-3xl sm:text-4xl font-semibold text-ink">{stat.value}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-inkmute leading-tight">{stat.label}</p>
            </div>
          ))}
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.15} className="group relative border border-paperline bg-white p-3">
          <div className="overflow-hidden">
            <img
              src={IMAGES.intro}
              alt="EhimoBCE building construction works"
              loading="lazy"
              className="img-treated w-full aspect-[4/5] object-cover group-hover:scale-105"
            />
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-inkmute">Building Construction — Nigeria</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const BrandPoster = () => (
  <section data-testid="brand-poster" className="bg-paper text-ink border-t border-paperline">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5">
        <SectionTag number="02" label="Our promise" light />
        <MaskedLines
          inView
          lines={['Building today.', 'Engineering', 'tomorrow.']}
          lineClassName="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold uppercase tracking-tight leading-[1.02]"
        />
        <Reveal delay={0.2} className="mt-8 max-w-md">
          <p className="text-inkmute leading-relaxed">Design, building and civil engineering — one team, one standard of delivery.</p>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal delay={0.15} className="border border-paperline bg-white p-3">
          <img
            src="/images/brand-poster.webp"
            alt="Ehimo Building & Civil Engineering — Building Today. Engineering Tomorrow. Design, Building, Civil Engineering."
            loading="lazy"
            className="w-full max-h-[75vh] object-cover"
          />
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesPreview = () => {
  const [active, setActive] = useState(0);
  return (
    <section data-testid="services-preview" className="bg-paper text-ink border-t border-paperline">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <SectionTag number="03" label="What we do" light />
            <MaskedLines
              inView
              lines={['Three disciplines.', 'One standard of delivery.']}
              lineClassName="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98]"
            />
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/services"
              data-testid="services-preview-cta"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors"
            >
              Explore our services <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 divide-y divide-paperline border-y border-paperline">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link
                  to="/services"
                  data-testid={`service-row-${s.id}`}
                  onMouseEnter={() => setActive(i)}
                  className="group flex items-center gap-6 lg:gap-10 py-8 lg:py-10"
                >
                  <span className="font-display text-sm text-ink bg-accent px-1.5 py-0.5">{s.number}</span>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl lg:text-4xl font-semibold uppercase tracking-tight text-ink">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-inkmute max-w-md">{s.summary}</p>
                  </div>
                  <ArrowUpRight
                    size={26}
                    className="shrink-0 text-ink/20 transition-all duration-300 group-hover:text-ink group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-28 border border-paperline bg-white p-3">
              <div className="overflow-hidden aspect-[4/3] relative">
                {SERVICES.map((s, i) => (
                  <img
                    key={s.id}
                    src={s.image}
                    alt={`${s.title} — Ehimo Building & Civil Engineering`}
                    loading="lazy"
                    className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${
                      active === i ? 'opacity-100 grayscale-0' : 'opacity-0 grayscale'
                    }`}
                  />
                ))}
              </div>
              <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-inkmute">{SERVICES[active].title}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturedProjects = () => {
  const featured = [PROJECTS[0], PROJECTS[1], PROJECTS[4], PROJECTS[5]];
  return (
    <section data-testid="featured-projects" className="bg-paper text-ink border-t border-paperline">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-16">
          <div>
            <SectionTag number="04" label="Selected work" light />
            <MaskedLines
              inView
              lines={['Completed &', 'on-going works.']}
              lineClassName="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98]"
            />
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/projects"
              data-testid="featured-projects-cta"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink font-medium border-b-2 border-accent pb-1 hover:bg-accent transition-colors"
            >
              All projects <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {featured.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <ProjectCard project={p} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SafetyBand = () => (
  <section data-testid="safety-band" className="bg-coal relative overflow-hidden">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <SectionTag number="05" label="Safety & standards" />
        <MaskedLines
          inView
          lines={['We adhere to', 'safety measures.']}
          lineClassName="font-display text-3xl sm:text-4xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.98]"
        />
        <Reveal delay={0.2} className="mt-8 max-w-lg">
          <p className="text-mute leading-relaxed">
            Every EhimoBCE project is delivered within the expected health & safety requirements — alongside agreed
            budget, schedule and quality standards. Our work is led by CORBON-registered builders and members of the
            Nigerian Institute of Building.
          </p>
          <Link
            to="/policies"
            data-testid="safety-policies-cta"
            className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-accent hover:text-accent-dim transition-colors"
          >
            Our policies <ArrowUpRight size={16} />
          </Link>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {[
            { icon: ShieldCheck, label: 'Health & Safety' },
            { icon: Award, label: 'CORBON Registered' },
            { icon: HardHat, label: 'NIOB Members' },
          ].map(({ icon: Icon, label }, i) => (
            <Reveal key={label} delay={0.3 + i * 0.1} y={12}>
              <span className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs uppercase tracking-[0.15em] text-white/80">
                <Icon size={14} className="text-accent" /> {label}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
      <div className="lg:col-span-6">
        <Reveal delay={0.15} className="group border border-line p-3">
          <div className="overflow-hidden">
            <img
              src={IMAGES.safety}
              alt="EhimoBCE site team adhering to health and safety measures"
              loading="lazy"
              className="img-treated w-full aspect-[16/10] object-cover group-hover:scale-105"
            />
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-[0.25em] text-mute">Health & Safety — On site</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const Clients = () => (
  <section data-testid="clients-section" className="bg-white text-ink border-t border-paperline">
    <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
      <SectionTag number="06" label="Who we have worked with" light />
      <div className="grid grid-cols-2 md:grid-cols-5 border border-paperline divide-x divide-y md:divide-y-0 divide-paperline">
        {CLIENTS.map((c, i) => (
          <Reveal key={c.name} delay={i * 0.06} className="flex items-center justify-center p-8 lg:p-10">
            <img
              src={c.logo}
              alt={`${c.name} — EhimoBCE client`}
              loading="lazy"
              className="max-h-12 w-auto opacity-70 hover:opacity-100 transition-opacity duration-500"
            />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  useSEO('', 'Ehimo Building & Civil Engineering is a dynamic building and civil engineering construction company delivering world class residential, commercial and industrial projects across Nigeria.');
  return (
    <Page>
      <Hero />
      <Intro />
      <BrandPoster />
      <Marquee items={['Building Construction', 'Design & Build', 'Civil Engineering', 'Lagos', 'Ilorin', 'Uyo', 'Ado-Ekiti', 'Ikorodu']} />
      <ServicesPreview />
      <FeaturedProjects />
      <SafetyBand />
      <Clients />
      <CTASection />
    </Page>
  );
}
